import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  BulkMakeupActivity,
  GetMakeupActivities,
  GetMakeupActivitiesByWeekly,
  UpdateMakeupActivity,
  SetMakeupActivityLoading,
  SetMakeupActivityError,
} from '@tsin-core/actions/makeup-activity.action';
import {
  MakeupActivityModel,
  MakeupActivityStateModel,
  LoadingMakeupActivityState,
} from '@tsin-core/models/makeup-activity.model';
import { MakeupActivityService } from '@tsin-core/services/http/makeup-activity.service';
import { tap, catchError } from 'rxjs';

@State<MakeupActivityStateModel>({
  name: 'makeupActivityState',
  defaults: {
    loading: LoadingMakeupActivityState.notLoading,
    makeupActivities: [],
    error: null,
  },
})
@Injectable()
export class MakeupActivityState {
  makeupActivityService: MakeupActivityService = inject(MakeupActivityService);

  @Selector()
  static getMakeupActivities(state: MakeupActivityStateModel) {
    return state.makeupActivities;
  }

  @Selector()
  static getLoading(state: MakeupActivityStateModel) {
    return state.loading;
  }

  @Selector()
  static getError(state: MakeupActivityStateModel) {
    return state.error;
  }

  @Action(GetMakeupActivities)
  getMakeupActivitiesAction(ctx: StateContext<MakeupActivityStateModel>, action: GetMakeupActivities) {
    ctx.patchState({ loading: LoadingMakeupActivityState.loadingList });

    return this.makeupActivityService.getMakeupActivities(action.payload).pipe(
      tap((results: MakeupActivityModel[]) => {
        ctx.patchState({
          makeupActivities: results,
          loading: LoadingMakeupActivityState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingMakeupActivityState.notLoading,
          error: error.message,
        });
        throw error;
      })
    );
  }

  @Action(GetMakeupActivitiesByWeekly)
  getMakeupActivitiesByWeekly(ctx: StateContext<MakeupActivityStateModel>, action: GetMakeupActivitiesByWeekly) {
    ctx.patchState({ loading: LoadingMakeupActivityState.loadingList });

    return this.makeupActivityService.getMakeupActivitiesByWeekly(action.payload).pipe(
      tap((results: MakeupActivityModel[]) => {
        ctx.patchState({
          makeupActivities: results,
          loading: LoadingMakeupActivityState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingMakeupActivityState.notLoading,
          error: error.message,
        });
        throw error;
      })
    );
  }

  @Action(UpdateMakeupActivity)
  updateMakeupActivity(ctx: StateContext<MakeupActivityStateModel>, action: UpdateMakeupActivity) {
    ctx.patchState({ loading: LoadingMakeupActivityState.loadingAddUpdate });

    return this.makeupActivityService.updateMakeupActivity(action.payload).pipe(
      tap((result: MakeupActivityModel) => {
        const state = ctx.getState();
        let activities = [...state.makeupActivities];
        const index = activities.findIndex((item) => item.id === action.payload.id);
        if (index >= 0) {
          activities[index] = result;
        }
        ctx.patchState({
          makeupActivities: activities,
          loading: LoadingMakeupActivityState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingMakeupActivityState.notLoading,
          error: error.message,
        });
        throw error;
      })
    );
  }

  @Action(SetMakeupActivityLoading)
  setLoading(ctx: StateContext<MakeupActivityStateModel>, action: SetMakeupActivityLoading) {
    ctx.patchState({ loading: action.loading });
  }

  @Action(SetMakeupActivityError)
  setError(ctx: StateContext<MakeupActivityStateModel>, action: SetMakeupActivityError) {
    ctx.patchState({ error: action.error });
  }

  /**
   * Submit makeup activity status for multiple learners in a single request.
   * Soumettre le statut de rattrapage pour plusieurs apprenants en une seule requête.
   */
  @Action(BulkMakeupActivity)
  bulkMakeupActivity(ctx: StateContext<MakeupActivityStateModel>, action: BulkMakeupActivity) {
    ctx.patchState({ loading: LoadingMakeupActivityState.loadingAddUpdate });
    return this.makeupActivityService.bulkMarkCompleted(action.payload).pipe(
      tap(() => {
        ctx.patchState({ loading: LoadingMakeupActivityState.notLoading, error: null });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingMakeupActivityState.notLoading, error: error.message });
        throw error;
      }),
    );
  }
}
